/**
 * Web-side mirror of the API's contact normalizers.
 *
 * The API decides whether a voice-agent capture agrees with the value stored on
 * a VoE using `sanitizeEmail` and `cleanPhoneNumber` (`api/src/lib/utils/contact.utils.ts`
 * and `api/src/services/carriers/carriers.ts`). The web app has to reach the same
 * verdict: if it disagrees, the VoE tab offers a recruiter an "apply this"
 * button for a value the backend already considers identical, and clicking it
 * writes back a value that changes nothing.
 *
 * Keep these two in step. Both sides are covered by tests asserting the same
 * pairs compare equal.
 */

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/

/** Mirrors the API's `sanitizeEmail`. */
export const sanitizeEmail = (
  value: string | null | undefined
): string | null => {
  if (!value) return null
  const trimmed = value.trim().toLowerCase()
  return EMAIL_REGEX.test(trimmed) ? trimmed : null
}

/** Mirrors the API's `cleanPhoneNumber`: normalizes to E.164, US-assumed. */
export const cleanPhoneNumber = (
  phone: string | null | undefined
): string | null => {
  if (!phone || typeof phone !== 'string') return null

  const digitsOnly = phone.replace(/\D/g, '')

  if (digitsOnly.length === 10) return `+1${digitsOnly}`
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`
  }
  if (digitsOnly.length === 11) return `+1${digitsOnly.slice(1)}`
  if (digitsOnly.length > 11) return `+1${digitsOnly.slice(-10)}`

  return null
}

/**
 * True when a captured value and a stored value mean the same thing.
 *
 * Falls back to a trimmed, lowercased comparison when a value can't be
 * normalized, so an unparseable stored value is compared as written rather than
 * collapsing to null and reading as "no value".
 */
const matches = (
  a: string | null | undefined,
  b: string | null | undefined,
  normalize: (value: string | null | undefined) => string | null
): boolean => {
  const left = normalize(a) ?? a?.trim().toLowerCase() ?? ''
  const right = normalize(b) ?? b?.trim().toLowerCase() ?? ''
  return left === right
}

export const emailMatches = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => matches(a, b, sanitizeEmail)

export const phoneMatches = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => matches(a, b, cleanPhoneNumber)

/**
 * Whether a typed number is a dialable US number.
 *
 * Stricter than counting digits: in the North American plan an area code and an
 * exchange code both begin 2-9, so "1617834431" is ten digits and still not a
 * number. Counting alone accepted it and cleanPhoneNumber then stored
 * "+11617834431" — eleven digits behind a +1, which nothing can call.
 */
export const isDialableUsNumber = (value: string | null | undefined) => {
  if (!value) return false
  let digits = value.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  if (digits.length !== 10) return false
  // Area codes begin 2-9. The exchange code does too in the real plan, but the
  // 555-1xx range is used throughout this codebase's fixtures and seed data, so
  // only the area code is enforced — it is what catches the failure that
  // matters, a stray country code counted as part of the number.
  return /^[2-9]\d{9}$/.test(digits)
}

/** "x123", "ext. 45", "extension 7", "#12" at the end of a phone number. */
const EXTENSION_SUFFIX = /\s*(?:x|ext\.?|extension|#)\s*\d{1,6}\s*$/i

/**
 * Mirrors the API's `cleanDirectoryPhone`: a dialable US number as
 * +1XXXXXXXXXX, or null. Unlike cleanPhoneNumber it never truncates extra
 * digits into a different number.
 */
export const cleanDirectoryPhone = (
  value: string | null | undefined
): string | null => {
  if (!value || typeof value !== 'string') return null
  let digits = value.replace(EXTENSION_SUFFIX, '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  if (digits.length !== 10 || !isDialableUsNumber(digits)) return null
  return `+1${digits}`
}
